import { sendHeartbeat } from "./heartbeat";

/**
 * Chat error policy, in one place: how a failure is described, what the visitor
 * is allowed to read, whether retrying can help, and what gets reported back.
 *
 * Every failure is normalised to:
 *   kind          — network | timeout | http | http_nonjson | parse | client
 *   code          — stable identifier: a server error code, or a client_* one
 *   status        — HTTP status (the server's intended one when it differs)
 *   serverMessage — the server's own words; NOT shown unless allowlisted below
 *   detail        — never shown to a visitor; console + heartbeat only
 */

// A plain augmented Error rather than a subclass: `class X extends Error` loses
// instanceof under some Babel targets, and nothing here needs a real subclass.
export function chatError(kind, code, status, serverMessage, detail) {
  const err = new Error(code + (serverMessage ? ": " + serverMessage : ""));
  err.isChatError = true;
  err.kind = kind;
  err.code = code || "client_unexpected";
  err.status = status || 0;
  err.serverMessage = serverMessage || "";
  err.detail = detail || "";
  return err;
}

// Anything that escapes a try block — including a bug in our own code — still
// has to render as an error notice rather than take the widget down.
export function describeError(error) {
  if (error && error.isChatError) return error;
  return chatError(
    "client",
    "client_unexpected",
    0,
    "",
    (error && error.message) || String(error)
  );
}

// Server copy written FOR a visitor, about something they caused or can act on.
// An allowlist, not a blocklist, so a server code added later defaults to safe
// generic copy rather than leaking operator detail ("OpenAI API key is not
// configured") into the widget.
const VISITOR_SAFE_CODES = {
  cmw_rate_limited: true,
  cmw_conversation_message_limit: true,
  cmw_conversation_char_limit: true,
  cmw_message_too_long: true,
  cmw_missing_message: true,
  cmw_disabled: true,
  // Transient upstream trouble. The copy for these comes from the server's
  // error registry and was written for visitors ("The assistant is busy right
  // now"), so it beats the generic fallback. The config-flavoured upstream
  // codes — auth, rejected, missing_key — stay generic: a visitor should not
  // be told the site is misconfigured.
  cmw_upstream_timeout: true,
  cmw_upstream_unreachable: true,
  cmw_upstream_rate_limited: true,
  cmw_upstream_unavailable: true,
  cmw_upstream_bad_response: true,
};

const COPY_BY_CODE = {
  cmw_invalid_nonce: "Your session has expired. Please refresh the page and try again.",
  cmw_invalid_origin: "Your session has expired. Please refresh the page and try again.",
  rest_cookie_invalid_nonce: "Your session has expired. Please refresh the page and try again.",
  client_offline: "You appear to be offline. Check your connection and try again.",
  client_network: "We couldn’t reach the chatbot. Check your connection and try again.",
  client_timeout: "That request took too long to answer. Please try again.",
};

const COPY_DEFAULT =
  "Something went wrong at our end and the chatbot couldn’t answer. Please try again in a moment.";

export function visitorMessage(err) {
  if (
    err.kind === "http" &&
    VISITOR_SAFE_CODES[err.code] &&
    err.serverMessage &&
    err.serverMessage.length <= 200
  ) {
    return err.serverMessage;
  }
  return COPY_BY_CODE[err.code] || COPY_DEFAULT;
}

// "cmw_upstream_timeout · 504". The status is dropped when the code already
// carries it, so client_http_502 doesn't render as "client_http_502 · 502".
export function errorCodeLabel(err) {
  const code = err.code || "client_unexpected";
  if (err.status && code.indexOf(String(err.status)) === -1) {
    return code + " · " + err.status;
  }
  return code;
}

// Retry is offered unless an immediate retry provably fails the same way. The
// server's own `retryable` flag wins when it sent one.
const NO_RETRY = {
  cmw_rate_limited: true,
  cmw_conversation_message_limit: true,
  cmw_conversation_char_limit: true,
  cmw_message_too_long: true,
  cmw_missing_message: true,
  cmw_disabled: true,
  cmw_invalid_nonce: true,
  cmw_invalid_origin: true,
  rest_cookie_invalid_nonce: true,
};

export function isRetryable(err) {
  if (typeof err.retryable === "boolean") return err.retryable;
  return !NO_RETRY[err.code];
}

// Only beacon when the server is implicated. A visitor's dropped wifi, or a
// visitor who tripped the rate limit, is not a widget failure — counting those
// would light the health pill for something nobody can fix, and would let one
// abusive visitor raise a false alarm.
function shouldHeartbeat(err) {
  if (err.kind === "network") return false;
  if (err.kind === "http") return err.status >= 500 || !VISITOR_SAFE_CODES[err.code];
  return true;
}

function heartbeatMessage(err, scope) {
  const text =
    (scope === "suggestions" ? "chat suggestions" : "chat") +
    " " +
    err.kind +
    " " +
    err.code +
    " (" +
    err.status +
    ")" +
    (err.ref ? " ref=" + err.ref : "") +
    (err.detail ? " — " + err.detail : "");
  return text.slice(0, 180);
}

// One suggestions beacon per page load: the follow-up call runs after every
// answer, so a systematically broken one would otherwise flood the counter.
let suggestionBeatSent = false;

export function reportChatError(restConfig, err, scope) {
  if (typeof console !== "undefined" && console.error) {
    console.error("Chatbot error", {
      scope: scope,
      kind: err.kind,
      code: err.code,
      status: err.status,
      ref: err.ref || "",
      serverMessage: err.serverMessage,
      detail: err.detail,
    });
  }

  if (!shouldHeartbeat(err)) return;

  if (scope === "suggestions") {
    if (suggestionBeatSent) return;
    suggestionBeatSent = true;
  }

  sendHeartbeat(restConfig, "error", heartbeatMessage(err, scope));
}
